import { TrustJetPlane, getAircraftImage, getAircraftCarouselImages } from '@assets/images';

/**
 * Get the aircraft model name from various possible data structures
 * @param {Object} aircraft - The aircraft object
 * @returns {string} The formatted model name or '-' if not available
 */
export const getAircraftModelName = (aircraft) => {
  if (!aircraft) return '-';

  // Custom-profile display name — set by getEffectiveAircraft when the
  // broker edited Aircraft Name via the pricing drawer (custom_profile.name).
  // Wins over every derived form so the user's free-text label surfaces
  // uniformly without disturbing make_model (still the lookup key for the
  // bundled aircraft-image fallback chain).
  if (aircraft.display_name_override) return aircraft.display_name_override;

  // Highest priority: top-level manufacturer + model when both are set
  // (e.g. JetNet-pulled aircraft store the clean MAKE + MODEL pair here).
  if (aircraft.manufacturer && aircraft.model) {
    return `${aircraft.manufacturer} ${aircraft.model}`.trim();
  }

  if (aircraft.aircraft_make_model) {
    const { manufacturer } = aircraft.aircraft_make_model;

    // Extract model number from make_model (e.g., "LR-45-45" -> "45-45")
    let modelNumber = '';
    if (aircraft.make_model) {
      const firstDashIndex = aircraft.make_model.indexOf('-');
      if (firstDashIndex !== -1) {
        modelNumber = aircraft.make_model.substring(firstDashIndex + 1); // Get everything after first dash
      }
    }

    const modelStr = `${manufacturer || ''} ${modelNumber}`.trim();
    return modelStr || '-';
  }

  const fallbackStr = `${aircraft.manufacturer || ''} ${aircraft.make_model || ''}`.trim();
  return fallbackStr || '-';
};

/**
 * Get the aircraft location from operator data
 * @param {Object} operator - The operator object
 * @returns {string} The formatted location (city, state) or empty string if not available
 */
export const getAircraftLocation = (operator) => {
  if (!operator) return '';

  const { faa_city, faa_state } = operator;

  if (!faa_city && !faa_state) return '';

  if (faa_city && faa_state) {
    return `${faa_city}, ${faa_state}`;
  }

  return faa_city || faa_state || '';
};

/**
 * Get the effective operator name for THIS quote. Falls through to the
 * base operator record's name when there's no per-quote override.
 *
 * Overrides live at `quote.files.operator_name_overrides[aircraftId]`
 * (see EditPricingDrawer's operator-name buffer + useQuoteProcessor's
 * persistAircraftProfiles-adjacent write). Deliberately NOT merged onto
 * the aircraft record via `custom_profile` — the comment on
 * getEffectiveAircraft below is definitive that operator identity stays
 * contractual to the base record. The override is display-only, scoped
 * to a single quote, and never mutates the operator/aircraft rows.
 *
 * @param {Object} operator     - The base operator object (from bid_option.operator or aircraft.operator).
 * @param {Object} [quote]      - The quote whose per-option overrides may apply.
 * @param {string} [aircraftId] - The aircraft this option belongs to (override key).
 * @returns {string} The overridden name, or the base name, or '' if none.
 */
export const getEffectiveOperatorName = (operator, quote, aircraftId) => {
  const overrides = quote?.files?.operator_name_overrides || null;
  if (overrides && aircraftId) {
    const override = overrides[aircraftId];
    if (override && String(override).trim()) return String(override).trim();
  }
  return operator?.name || '';
};

/**
 * Get the operator name with location. Routes the name through the
 * per-quote override helper when a quote + aircraftId are passed;
 * legacy callers that omit those args get the base record's name (the
 * original single-arg behaviour, still valid for surfaces that show
 * canonical operator identity, e.g. /operators listings).
 * @param {Object} operator     - The operator object
 * @param {Object} [quote]      - The quote whose override may apply
 * @param {string} [aircraftId] - The aircraft this operator is attached to
 * @returns {string} The formatted operator name with location (e.g., "Operator Name • City, State")
 */
export const getOperatorWithLocation = (operator, quote, aircraftId) => {
  if (!operator) return '-';

  const name = getEffectiveOperatorName(operator, quote, aircraftId) || '-';
  const location = getAircraftLocation(operator);

  if (location && location !== 'null') {
    return `${name} • ${location}`;
  }

  return name;
};

/**
 * Get the aircraft type from various possible data structures
 * @param {Object} aircraft - The aircraft object (can be from aircraftDetails or tail)
 * @returns {string} The aircraft type or '-' if not available
 */
export const getAircraftType = (aircraft) => {
  if (!aircraft) return '-';
  return aircraft?.aircraft_make_model?.aircraft_type || aircraft?.aircraft_type || '-';
};

/**
 * Get a property value from aircraft object with support for multiple fallback property names
 * @param {Object} aircraft - The aircraft object (can be from aircraftDetails or tail)
 * @param {string[]} properties - Array of property names to check in order
 * @param {string} [suffix] - Optional suffix to append to the value (e.g., ' nm', ' Passengers')
 * @returns {string} The property value with optional suffix, or '-' if not available
 */
export const getAircraftProperty = (aircraft, properties, suffix = '') => {
  if (!aircraft) return '-';

  for (const prop of properties) {
    const value = aircraft[prop];
    if (value !== null && value !== undefined && value !== '') {
      return suffix ? `${value}${suffix}` : String(value);
    }
  }

  return '-';
};

const API_BASE_URL = process.env.REACT_APP_IMAGE_BASE_URL;

export const getAircraftPhotos = (aircraft, isCarousel = false) => {
    if (!aircraft) {
      return isCarousel ? [TrustJetPlane] : `${window.location.origin}/images/bgplaceholdernew.jpg`;
    }

    // First priority: image_urls from API
    if (aircraft?.image_urls && aircraft?.image_urls.length > 0) {
      if(isCarousel)
      {
        return aircraft.image_urls.map(url => formatImagUrl(url));
      }
      return formatImagUrl(aircraft.image_urls[0]);
    }

    // Second priority: local aircraft image by make_model
    const makeModel = aircraft?.aircraft_make_model?.make_model || aircraft?.make_model;
    if (makeModel) {
      if (isCarousel) {
        return getAircraftCarouselImages(makeModel);
      }
      return getAircraftImage(makeModel);
    }

    return isCarousel ? [TrustJetPlane] : `${window.location.origin}/images/bgplaceholdernew.jpg`;
};

export const formatImagUrl = (path) => {
   // Pass through fully-qualified URLs and in-memory previews unchanged:
   // http(s) URLs are already resolvable; blob: URLs come from the pricing
   // drawer while a broker's freshly-picked photo is still buffered in
   // formData (not yet uploaded to S3 — the preview save flow flushes
   // them), and data: URLs are inline base64 assets. Only relative S3
   // keys need REACT_APP_IMAGE_BASE_URL prepended.
   if (!path) return path;
   if (
     path.startsWith('http://') ||
     path.startsWith('https://') ||
     path.startsWith('blob:') ||
     path.startsWith('data:')
   ) {
     return path;
   }
   // Bundled public-folder photos ("/images/aircraft/…") are app-owned
   // assets served from the same origin — resolve against
   // window.location.origin rather than the S3 CDN base URL, so
   // suggestions the manual-add wizard persists as relative paths still
   // display across environments.
   if (path.startsWith('/images/')) {
     return `${window.location.origin}${path}`;
   }
   return `${API_BASE_URL}${path}`;
};

/**
 * Merge the caller-org's `custom_profile` onto a platform-catalog
 * aircraft record. The BE joins `custom_profile` onto the aircraft when
 * the request opts into `?include=custom_profile` — a null value means
 * the org hasn't authored a profile for this aircraft, so we return the
 * base record untouched.
 *
 * Fields the profile can override (all sourced from the profile row):
 *   - name              → drives the display name (via display_name_override)
 *   - description
 *   - tail_number       (first-class column on custom_profile)
 *   - image_urls
 *   - banner_image_url
 *   - extra_attributes.amenities    (string[])
 *   - extra_attributes.notes        (string)
 *
 * Not merged:
 *   - Operator (`aircraft.operator`) — always locked to the base record;
 *     operator identity is contractual, not cosmetic.
 *   - Anything not listed above.
 *
 * Every read surface that renders aircraft should call this helper before
 * pulling display fields. Callers no longer need to pass the quote —
 * `custom_profile` lives on the aircraft record itself.
 *
 * @param {Object} aircraft - Aircraft record with `custom_profile` include.
 * @returns Merged aircraft with profile fields taking precedence, or the
 *   base record unchanged when no profile is set.
 */
export const getEffectiveAircraft = (aircraft) => {
  if (!aircraft) return aircraft;
  const p = aircraft.custom_profile || null;
  // Even without a custom_profile we still need to lift owned-aircraft
  // extras (name / notes stored in aircraft.extra_attributes) onto the
  // merged view, so no early return here — every branch below is
  // guarded on whether its source exists.

  const merged = { ...aircraft };
  const extra = (p?.extra_attributes && typeof p.extra_attributes === 'object') ? p.extra_attributes : {};

  // Display name only — never overwrites make_model / manufacturer.
  // Those fields are lookup keys for the bundled aircraft-image fallback
  // chain (getAircraftPhotos / resolveAircraftImageUrl / AircraftImage)
  // which is keyed on canonical model strings like "CE-550-550".
  // Clobbering them with the broker's free-text name would break the
  // fallback for any aircraft whose S3 image_urls fail to load. Instead
  // we stash the override on a private field that getAircraftModelName
  // checks first — every screen that reads the model name goes through it.
  // Display name — priority chain:
  //   custom_profile.name         (platform-catalog org override)
  //   aircraft.extra_attributes.name  (owned-aircraft broker label — the
  //                                   free-text "Aircraft Name" from the
  //                                   pricing drawer for aircraft the
  //                                   org owns; no dedicated column
  //                                   exists on the aircraft table)
  //   (none — downstream falls through to make_model/manufacturer)
  const aircraftExtras = (aircraft.extra_attributes && typeof aircraft.extra_attributes === 'object')
    ? aircraft.extra_attributes : {};
  const nameOverride =
    (typeof p?.name === 'string' && p.name) ? p.name :
    (typeof aircraftExtras.name === 'string' && aircraftExtras.name) ? aircraftExtras.name :
    null;
  if (nameOverride) {
    merged.display_name_override = nameOverride;
  }
  if (typeof p?.description === 'string') {
    merged.description = p.description;
  }
  if (Array.isArray(p?.image_urls)) {
    merged.image_urls = p.image_urls;
  }
  if (p?.banner_image_url != null) {
    merged.banner_image_url = p.banner_image_url;
  }

  // Amenities live in extra_attributes.amenities as a string[]. The list
  // is DEFINITIVE — it represents exactly what the broker wants to show
  // on this quote, including "I removed lavatory even though the base
  // aircraft has it". We synthesize the structured has_* booleans from
  // the list (via splitAmenitiesToStructured, same lexicon the write
  // side uses) and REPLACE the base structured columns, so consumers
  // that read `amenities?.has_enclosed_lavatory` directly (QuotePreview
  // icons, TripSheet, itinerary email) reflect the broker's set.
  // Missing amenities → false in the merged view; base row untouched.
  if (Array.isArray(extra.amenities)) {
    const list = extra.amenities;
    const { bools, remaining } = splitAmenitiesToStructured(list);
    merged.aircraft_amenities = {
      // Keep identity + timestamps for anything that keys on them; replace
      // every semantic amenity field (all has_*, plus additional_amenities).
      ...(aircraft.aircraft_amenities || {}),
      ...bools,
      additional_amenities: remaining.length > 0 ? remaining.join(', ') : null,
    };
    merged.aircraft_features = {
      ...(aircraft.aircraft_features || {}),
      custom_features: list,
    };
  }

  // Broker note shown above amenities on cards / emails — priority
  // chain mirrors the display-name lookup:
  //   custom_profile.extra_attributes.notes  (platform-catalog org override)
  //   aircraft.extra_attributes.notes         (owned-aircraft broker note;
  //                                            no dedicated column exists)
  const notesOverride =
    (typeof extra?.notes === 'string' && extra.notes) ? extra.notes :
    (typeof aircraftExtras.notes === 'string' && aircraftExtras.notes) ? aircraftExtras.notes :
    null;
  if (notesOverride) {
    merged.quote_notes = notesOverride;
  }

  // Tail number — first-class column on custom_profile.
  if (typeof p?.tail_number === 'string' && p.tail_number) {
    merged.tail_number = p.tail_number;
  }

  return merged;
};


/**
 * Lexicon of predefined aircraft amenities — pattern list per boolean
 * column on `aircraft_amenities`. Case-insensitive exact-match against
 * a trimmed chip. Add synonyms here (not to callsites).
 *
 * When the broker types (or leaves hydrated) a chip that matches one
 * of these patterns, persistAircraftProfiles flips the structured
 * boolean instead of writing it into additional_amenities[] — keeps
 * the aircraft_amenities row semantically correct and lets the /tails
 * details drawer's toggle-derived rendering pick it up automatically.
 */
export const AMENITY_MATCHERS = [
  { key: 'has_wifi', label: 'Wi-Fi', patterns: ['wifi', 'wi-fi', 'wi fi', 'internet'] },
  { key: 'has_entertainment_system', label: 'Entertainment System', patterns: ['entertainment', 'entertainment system', 'ife', 'in-flight entertainment'] },
  { key: 'has_full_galley', label: 'Full Galley', patterns: ['full galley', 'galley', 'kitchen'] },
  { key: 'has_enclosed_lavatory', label: 'Enclosed Lavatory', patterns: ['enclosed lavatory', 'lavatory', 'lav', 'bathroom', 'restroom'] },
  { key: 'has_satellite_phone', label: 'Satellite Phone', patterns: ['satellite phone', 'sat phone', 'satphone', 'satellite'] },
];

/**
 * Split a free-form chip list into structured boolean amenities +
 * remaining free-form strings. Returns:
 *   { bools: {has_wifi, has_entertainment_system, has_full_galley,
 *             has_enclosed_lavatory, has_satellite_phone},
 *     remaining: string[] }
 *
 * Anything that matches an AMENITY_MATCHERS pattern (case-insensitive
 * exact match on trimmed value) flips the corresponding boolean and
 * is REMOVED from the remaining list — so "wifi" doesn't end up
 * duplicated in both has_wifi=true AND additional_amenities.
 */
export const splitAmenitiesToStructured = (chips = []) => {
  const bools = {
    has_wifi: false,
    has_entertainment_system: false,
    has_full_galley: false,
    has_enclosed_lavatory: false,
    has_satellite_phone: false,
  };
  const remaining = [];
  const list = Array.isArray(chips) ? chips : [];
  for (const item of list) {
    const lower = String(item || '').trim().toLowerCase();
    if (!lower) continue;
    const matcher = AMENITY_MATCHERS.find((m) => m.patterns.includes(lower));
    if (matcher) {
      bools[matcher.key] = true;
    } else {
      remaining.push(String(item).trim());
    }
  }
  return { bools, remaining };
};

/**
 * Reverse of splitAmenitiesToStructured: given an `aircraft_amenities`
 * row (or anything shaped like it — the 5 has_* bools + additional_amenities),
 * return a flat chip list with the structured booleans expanded into
 * their canonical labels (in AMENITY_MATCHERS order) followed by the
 * free-form additional amenities. Used by EditPricingDrawer's
 * hydration so an owned-aircraft edit shows Wi-Fi as a chip even
 * though the persisted row stores it as has_wifi=true.
 */
export const amenitiesFromStructured = (am) => {
  if (!am) return [];
  const structured = AMENITY_MATCHERS
    .filter((m) => am[m.key])
    .map((m) => m.label);
  const extras = Array.isArray(am.additional_amenities)
    ? am.additional_amenities.filter(Boolean).map((s) => String(s).trim()).filter(Boolean)
    : (typeof am.additional_amenities === 'string'
        ? am.additional_amenities.split(',').map((s) => s.trim()).filter(Boolean)
        : []);
  return [...structured, ...extras];
};


// Async URL probe using new Image(). Resolves true on load, false on
// error/timeout. Used by resolveAircraftImageUrl (email builders) and by UI
// components that want to hide broken URLs — e.g. the +N PHOTOS indicator
// on the Selected Flight card should count only URLs that actually load.
export const probeImageUrl = (url, timeoutMs = 5000) =>
  new Promise((resolve) => {
    if (!url) { resolve(false); return; }
    const img = new window.Image();
    let done = false;
    const finish = (ok) => {
      if (done) return;
      done = true;
      resolve(ok);
    };
    img.onload = () => finish(true);
    img.onerror = () => finish(false);
    setTimeout(() => finish(false), timeoutMs);
    img.src = url;
  });

/**
 * Async variant of getAircraftPhotos used by email templates: probes the
 * primary URL (S3 photo) and falls back to the bundled make_model image if
 * it 404s or CORS-fails. Emails need a URL that WILL load in the recipient's
 * client — we can't retry there, so pick the right one at generation time.
 */
export const resolveAircraftImageUrl = async (aircraft) => {
  const primary = getAircraftPhotos(aircraft);
  if (primary && await probeImageUrl(primary)) return primary;
  const makeModel = aircraft?.aircraft_make_model?.make_model || aircraft?.make_model;
  if (makeModel) {
    const bundled = getAircraftImage(makeModel);
    if (bundled && await probeImageUrl(bundled)) return bundled;
  }
  return `${window.location.origin}/images/bgplaceholdernew.jpg`;
};
